import {isLoggedIn} from '@github-ui/client-env'
import {useQueryClient} from '@github-ui/react-query'
import {dispatchRetransmitReact, dispatchSearch} from '@github-ui/search-bar-events'
import {
  APPEND_AND_FOCUS_EVENT,
  type AppendAndFocusEventDetail,
  PROVIDE_FEEDBACK_EVENT,
  REACT_CONNECTED_EVENT,
  REACT_DISCONNECTED_EVENT,
  UPDATE_INPUT_EVENT,
} from '@github-ui/search-bar-events/events'
import {type Dispatch, type SetStateAction, useCallback, useEffect, useEffectEvent, useRef} from 'react'

import {type SavedSearch, savedSearchesQueryOptions} from './suggestions/queries/saved-search-suggestions-query'

type SavedSearchNavigationParams = {
  savedSearches?: string
  expandedQuery?: string
}

function parseSavedSearchesFromUrl(): SavedSearch[] {
  try {
    const parsed = JSON.parse(new URLSearchParams(window.location.search).get('saved_searches') || '[]')
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed.filter(
      value =>
        typeof value === 'object' &&
        value !== null &&
        typeof (value as SavedSearch).name === 'string' &&
        typeof (value as SavedSearch).query === 'string',
    ) as SavedSearch[]
  } catch {
    return []
  }
}

function findSavedSearch(scopes: SavedSearch[], scopeName: string): SavedSearch | undefined {
  return scopes.find(scope => scope.name === scopeName || `"${scope.name}"` === scopeName)
}

// Cheap synchronous heuristic for whether a query contains a `saved:` custom-scope
// qualifier. Ordinary queries can skip the async saved-search fetch and parser
// import entirely; the authoritative parse still runs for queries that match.
function hasSavedSearchQualifier(query: string): boolean {
  return /\bsaved:/i.test(query)
}

async function deriveSavedSearchNavigationParams(
  query: string,
  scopesFromApi: SavedSearch[],
): Promise<SavedSearchNavigationParams> {
  try {
    const parsing = await import('@github-ui/blackbird-query-parsing/parsing')
    const ast = parsing.parseString(query)
    const customScopeNames = parsing.getCustomScopeNames(ast)
    if (customScopeNames.length === 0) {
      return {}
    }

    const scopesFromUrl = parseSavedSearchesFromUrl()

    const customScopes: SavedSearch[] = []
    for (const customScopeName of customScopeNames) {
      const customScope =
        findSavedSearch(scopesFromApi, customScopeName) || findSavedSearch(scopesFromUrl, customScopeName)
      if (customScope) {
        customScopes.push({name: customScope.name, query: customScope.query})
      }
    }

    if (customScopes.length === 0) {
      return {}
    }

    return {
      savedSearches: JSON.stringify(customScopes),
      expandedQuery: parsing.getExpandedQuery(query, customScopes, ast),
    }
  } catch {
    return {}
  }
}

/**
 * Determines the search result type for a query. Queries that are scoped to an
 * enterprise, org (`org`/`owner`/`user`), repo, or saved search (`saved`) default
 * to code search; unscoped queries default to repository search. This mirrors the
 * canonical `chooseSearchType` behaviour in `@github-ui/blackbird-query-parsing`.
 */
export function getSearchType(query: string): 'code' | 'repositories' {
  return /(?:^|\s)(?:enterprise|org|owner|user|repo|saved):/i.test(query) ? 'code' : 'repositories'
}

/**
 * Builds the `/search?…` href for a full-page navigation given the raw query and
 * any resolved saved-search parameters. All values are URL-encoded via
 * `URLSearchParams`.
 */
export function buildSearchHref(query: string, params: SavedSearchNavigationParams): string {
  const searchParams = new URLSearchParams({q: query, type: getSearchType(query)})
  const {savedSearches: encodedSavedSearches, expandedQuery} = params
  if (encodedSavedSearches && expandedQuery) {
    searchParams.set('saved_searches', encodedSavedSearches)
    searchParams.set('expanded_query', expandedQuery)
  }
  return `/search?${searchParams.toString()}`
}

export interface QuickSearchOpenOptions {
  retainScrollPosition?: boolean
  returnTarget?: HTMLElement
}

export interface UseQuickSearchBarEventsOptions {
  /** When false the hook is inert (no listeners, no dispatches). */
  enabled: boolean
  /** The query derived from the current search-results URL, used as the append base. */
  searchPageQuery: string
  /** Sets the query pushed into QuickSearch by external events. */
  onSetQuery: Dispatch<SetStateAction<string | null>>
  /** Opens the QuickSearch dialog (e.g. in response to append-and-focus). */
  onRequestOpen: (options?: QuickSearchOpenOptions) => void
  /** Opens the feedback dialog from shared search-bar events. */
  onRequestFeedback?: () => void
}

export interface QuickSearchNavigateOptions {
  openInNewWindow?: boolean
}

export interface UseQuickSearchBarEventsResult {
  /**
   * Runs a search. When a React results page is mounted it soft-navigates via
   * {@link SEARCH_EVENT}; otherwise it falls back to a full page navigation.
   */
  navigateToSearch: (query: string, options?: QuickSearchNavigateOptions) => void
}

/**
 * The search-bar half of the `blackbird_monolith_*` window-event contract,
 * mirroring `useSearchBarEvents` (the results-page half) in reverse. This lets
 * QuickSearch fully replace the legacy `<qbsearch-input>` element.
 *
 * Must be mounted by the always-present search entry point (not by QuickSearch
 * itself, which only mounts while open) so that events which need to *open* the
 * dialog are always heard.
 */
export function useQuickSearchBarEvents({
  enabled,
  searchPageQuery,
  onSetQuery,
  onRequestOpen,
  onRequestFeedback,
}: UseQuickSearchBarEventsOptions): UseQuickSearchBarEventsResult {
  // Whether a React search-results page is mounted. When true, searches are
  // routed through it (soft nav) instead of a full page load.
  const isReactContextRef = useRef(false)
  // `queryClient.fetchQuery` is used for on-demand saved-search fetches; it
  // honors `staleTime` (returns fresh cached data without re-requesting) and
  // deduplicates in-flight requests, so a long-lived page never expands a
  // renamed scope using stale data. No `useQuery` observer is registered here
  // because this hook is mounted by the always-present global search entry
  // point, and an active observer would request `/search/custom_scopes` on
  // every logged-in page.
  const queryClient = useQueryClient()

  const handleUpdateInput = useEffectEvent((event: Event) => {
    if (event instanceof CustomEvent) {
      onSetQuery(typeof event.detail === 'string' ? event.detail : '')
    }
  })

  const handleAppendAndFocus = useEffectEvent((event: Event) => {
    if (!(event instanceof CustomEvent)) return

    const {appendQuery, retainScrollPosition, returnTarget} = (event.detail ?? {}) as AppendAndFocusEventDetail
    onSetQuery(previousQuery => {
      const query = (previousQuery ?? searchPageQuery).trim()
      return appendQuery && !query.endsWith(appendQuery) ? `${query} ${appendQuery}`.trim() : query
    })
    onRequestOpen({retainScrollPosition, returnTarget})
  })

  const handleProvideFeedback = useEffectEvent(() => {
    onRequestFeedback?.()
  })

  useEffect(() => {
    if (!enabled) {
      isReactContextRef.current = false
      return
    }

    // Track whether a React results page is mounted (mirrors qbsearch-input's
    // `#isReactContext`).
    const handleReactConnected = () => {
      isReactContextRef.current = true
    }
    const handleReactDisconnected = () => {
      isReactContextRef.current = false
    }

    window.addEventListener(REACT_CONNECTED_EVENT, handleReactConnected)
    window.addEventListener(REACT_DISCONNECTED_EVENT, handleReactDisconnected)
    window.addEventListener(UPDATE_INPUT_EVENT, handleUpdateInput)
    window.addEventListener(APPEND_AND_FOCUS_EVENT, handleAppendAndFocus)
    window.addEventListener(PROVIDE_FEEDBACK_EVENT, handleProvideFeedback)

    // Announce to any already-mounted results page that the bar is present, so
    // it replies with `REACT_CONNECTED_EVENT`.
    dispatchRetransmitReact()

    return () => {
      isReactContextRef.current = false
      window.removeEventListener(REACT_CONNECTED_EVENT, handleReactConnected)
      window.removeEventListener(REACT_DISCONNECTED_EVENT, handleReactDisconnected)
      window.removeEventListener(UPDATE_INPUT_EVENT, handleUpdateInput)
      window.removeEventListener(APPEND_AND_FOCUS_EVENT, handleAppendAndFocus)
      window.removeEventListener(PROVIDE_FEEDBACK_EVENT, handleProvideFeedback)
    }
  }, [enabled])

  const navigateToSearch = useCallback(
    (query: string, options?: QuickSearchNavigateOptions) => {
      const trimmed = query.trim()
      if (!trimmed) return

      const openInNewWindow = options?.openInNewWindow ?? false

      const performNavigation = (params: SavedSearchNavigationParams) => {
        const {savedSearches: encodedSavedSearches, expandedQuery} = params

        if (isReactContextRef.current && !openInNewWindow) {
          // Let the mounted React results page perform a soft navigation. `p`/`l`
          // are reset so a new search jumps back to page one (mirrors qbsearch).
          dispatchSearch(trimmed, {
            p: null,
            l: null,
            type: getSearchType(trimmed),
            ['saved_searches']: encodedSavedSearches,
            ['expanded_query']: expandedQuery,
          })
          return
        }

        const href = buildSearchHref(trimmed, params)
        if (openInNewWindow) {
          window.open(href, '_blank', 'noopener,noreferrer')
        } else {
          window.location.assign(href)
        }
      }

      // Fast path: ordinary queries (no `saved:` qualifier) navigate synchronously
      // without fetching saved searches or importing the query parser.
      if (!hasSavedSearchQualifier(trimmed)) {
        performNavigation({})
        return
      }

      void (async () => {
        let savedSearches: SavedSearch[] = []
        if (isLoggedIn()) {
          try {
            savedSearches = await queryClient.fetchQuery(savedSearchesQueryOptions())
          } catch {
            // ignore fetch errors – fall back to empty list
          }
        }

        const params = await deriveSavedSearchNavigationParams(trimmed, savedSearches)
        performNavigation(params)
      })()
    },
    [queryClient],
  )

  return {navigateToSearch}
}
